import React from 'react';
import { Link } from 'react-router-dom';
import { useHomepageSettings } from '../../hooks/useHomepageSettings';

interface ServicePreview {
  image: string;
  title: string;
  description: string;
  link: string;
}

const ServicesPreview: React.FC = () => {
  const { data: settings, isLoading } = useHomepageSettings();

  const defaultHeading = 'Our Services';
  const defaultSubheading =
    'Comprehensive care for your second home, from routine monitoring to white-glove concierge experiences.';
  const defaultServices: ServicePreview[] = [
    {
      image:
        'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?q=80&w=2074',
      title: 'Home Watch Services',
      description:
        'Regular property inspections, maintenance coordination, and 24/7 monitoring to protect your vacation home investment.',
      link: '/services/home-watch',
    },
    {
      image:
        'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2075',
      title: 'Concierge Services',
      description:
        'Arrival preparation, departure cleanup, travel arrangements, and personalized services to make every visit effortless.',
      link: '/services/concierge',
    },
  ];

  const displayHeading = settings?.servicesPreview?.heading || defaultHeading;
  const displaySubheading =
    settings?.servicesPreview?.subheading || defaultSubheading;

  // Map Sanity services to component format
  const sanityServices: ServicePreview[] = [];
  if (settings?.servicesPreview?.homeWatch) {
    sanityServices.push({
      image:
        settings.servicesPreview.homeWatch.image?.asset?.url ||
        defaultServices[0]?.image ||
        '',
      title: settings.servicesPreview.homeWatch.title,
      description: settings.servicesPreview.homeWatch.description,
      link: settings.servicesPreview.homeWatch.link,
    });
  }
  if (settings?.servicesPreview?.concierge) {
    sanityServices.push({
      image:
        settings.servicesPreview.concierge.image?.asset?.url ||
        defaultServices[1]?.image ||
        '',
      title: settings.servicesPreview.concierge.title,
      description: settings.servicesPreview.concierge.description,
      link: settings.servicesPreview.concierge.link,
    });
  }

  const displayServices =
    sanityServices.length > 0 ? sanityServices : defaultServices;
  const isEnabled = settings?.servicesPreview?.enabled !== false;

  // Don't render if disabled
  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <section id="services" className="py-20 md:py-28 bg-white scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 md:py-28 bg-white scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4">
            {displayHeading}
          </h2>
          <p className="text-xl text-sand-700 max-w-2xl mx-auto">
            {displaySubheading}
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {displayServices.map((service, index) => (
            <Link
              key={index}
              to={service.link}
              className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2"
            >
              {/* Background Image */}
              <div className="relative h-[500px] overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary-700/95 via-primary-700/70 to-transparent" />
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-3 group-hover:text-primary-200 transition-colors">
                  {service.title}
                </h3>
                <p className="text-white/90 text-lg leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Learn More Button */}
                <div className="inline-flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all">
                  Learn More
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>
              </div>

              {/* Decorative Corner Accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/20 to-transparent transform rotate-45 translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-500" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
